// packages/utils/src/client/reportClientError.ts
import { stripUrlFragment } from '@tourtask/utils/shared/urls/stripUrlFragment';

/**
 * Best-effort client-error telemetry.
 *
 * A browser-side React throw (render/hydration error, ChunkLoadError, etc.)
 * never reaches the server, so Next.js's generic "a client-side exception
 * occurred" overlay leaves no trace in the server log. This helper POSTs a small
 * JSON payload to the app's `/api/client-error` sink so those throws become
 * visible (and diagnosable) in pm2.
 *
 * It uses `navigator.sendBeacon` when available — that survives the page being
 * torn down by an error-boundary reload — and falls back to a keepalive `fetch`.
 * It NEVER throws: telemetry must not become a second failure stacked on the
 * first.
 */

export type ClientErrorReport = {
  message: string;
  /** One of: 'render' | 'global-error' | 'chunk-load' | 'chunk-load-giveup' | 'login-page-signed-in' | … */
  kind?: string;
  stack?: string;
  /** Next.js attaches `digest` to errors surfaced through an error boundary. */
  digest?: string;
  /** React component stack, when the caller has one (error boundaries). */
  componentStack?: string;
};

const ENDPOINT = '/api/client-error';

export function reportClientError(report: ClientErrorReport): void {
  if (typeof window === 'undefined') return;

  try {
    const payload = {
      ...report,
      // Fragment dropped: capability-link tokens and booking tokens live there.
      url: window.location?.href !== undefined ? stripUrlFragment(window.location.href) : undefined,
      hadFragment: (window.location?.hash ?? '').length > 0,
      // Pathname split out so logs/greps can group crashes by route without
      // parsing the full URL (and without the query string's noise).
      pathname: window.location?.pathname,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      // Which build threw. Lets us (a) tell a stale-tab version-skew crash from a
      // genuine bug, and (b) know which deploy's source maps to resolve the stack
      // against. Inlined at build time from next.config's NEXT_PUBLIC_DEPLOY_ID.
      deployId: process.env.NEXT_PUBLIC_DEPLOY_ID || undefined,
      ts: new Date().toISOString(),
    };
    const body = JSON.stringify(payload);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      // sendBeacon queues the request so it still flushes if the page is about
      // to reload/unload (the common case right after an error).
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* swallow — telemetry is best-effort */
    });
  } catch {
    /* never throw from telemetry */
  }
}

/** Coerce an unknown thrown value into a {@link ClientErrorReport}. */
export function toClientErrorReport(
  error: unknown,
  extra?: Partial<ClientErrorReport>,
): ClientErrorReport {
  const base: ClientErrorReport =
    error instanceof Error
      ? { message: error.message, stack: error.stack }
      : { message: typeof error === 'string' ? error : 'Unknown client error' };

  const digestValue: unknown =
    error && typeof error === 'object' && 'digest' in error ? error.digest : undefined;
  const digest = typeof digestValue === 'string' && digestValue !== '' ? digestValue : undefined;

  return { ...base, ...(digest ? { digest } : {}), ...extra };
}
